import { useRouter } from "next/router";
import { AuthorizedRevenueChart } from "./AuthorizedRevenueChart";
import { BrandDistributionChart } from "../Charts/BrandDistributionChart";
import { CapturedRevenueChart } from "./CapturedRevenueChart";
import { DailyDeclinesChart } from "./DailyDeclinesChart";
import { DeclineReasonsChart } from "../Charts/DeclineReasonsChart";
import { IssuingCountriesChart } from "../Charts/IssuingCountriesChart";
import { MethodsChart } from "./MethodsChart";
import { ProvidersChart } from "./ProvidersChart";
import { RecoveryRateCard } from "../Charts/RecoveryRateCard";
import { RefundedRevenueChart } from "./RefundedRevenueChart";
import { TopIssuersChart } from "../Charts/TopIssuersChart";
import { TotalTransactionsChart } from "../Charts/TotalTransactionsChart";
import { useMemo } from "react";
import { isSameDay } from "date-fns";
import { useFeatureFlags } from "../../../../hooks/query";
import { Section } from "../Section";
import { ChartsProvider } from "../Charts/shared/ChartsProvider";

export function DatadogAnalytics({
  revenueSectionRef,
  declinesSectionRef,
  recoverySectionRef,
  brandsSectionRef,
}: {
  revenueSectionRef?: React.RefObject<HTMLDivElement>;
  declinesSectionRef?: React.RefObject<HTMLDivElement>;
  recoverySectionRef?: React.RefObject<HTMLDivElement>;
  brandsSectionRef?: React.RefObject<HTMLDivElement>;
} = {}) {
  const { featureFlags } = useFeatureFlags();
  const router = useRouter();
  const { start_date, end_date } = router.query;

  const isSameDayRange = useMemo(() => {
    if (!start_date || !end_date) return false;

    return isSameDay(
      new Date(start_date as string),
      new Date(end_date as string)
    );
  }, [start_date, end_date]);

  return (
    <ChartsProvider>
      <div className="mt-6 flex flex-col gap-6">
        {featureFlags.datadog_dashboard_analytics && (
          <>
            <Section ref={revenueSectionRef} id="revenue-section">
              <Section.Title>Revenue Overview</Section.Title>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <ProvidersChart />
                <MethodsChart />
              </div>
              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="col-span-full">
                  <TotalTransactionsChart />
                </div>
                <CapturedRevenueChart isSameDayRange={isSameDayRange} />
                <AuthorizedRevenueChart isSameDayRange={isSameDayRange} />
                <RefundedRevenueChart isSameDayRange={isSameDayRange} />
              </div>
            </Section>

            <Section ref={declinesSectionRef} id="declines-section">
              <Section.Title>Decline overview</Section.Title>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <DeclineReasonsChart />
                <DailyDeclinesChart />
              </div>
            </Section>
          </>
        )}

        {featureFlags.new_dashboard_rates_from_marts && (
          <Section ref={recoverySectionRef} id="recovery-section">
            <Section.Title>Recovery overview</Section.Title>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <RecoveryRateCard
                chartType="RECOVERY_RATE"
                title="Recovery rate"
                description="Recovered intents ÷ all retried intents. Answers: when we retry a failed transaction, what percentage is successful."
              />
              <RecoveryRateCard
                chartType="OVERALL_SOFT_DECLINE_RECOVERY"
                title="Overall soft decline recovery"
                description="Recovered ÷ every first-try soft decline, includes soft declines we never retried. Answers: from all soft declines, what percentage did we recover."
              />
              <RecoveryRateCard
                chartType="SOFT_DECLINE_COVERAGE"
                title="Soft decline coverage"
                description="Retried ÷ every first-try soft decline. Answers: how many recoverable declines did we actually retry? Low % means recovery can be configured better."
              />
            </div>
          </Section>
        )}

        {featureFlags.datadog_dashboard_analytics && (
          <Section ref={brandsSectionRef} id="brands-section">
            <Section.Title>Brands overview</Section.Title>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <BrandDistributionChart />
              <IssuingCountriesChart />
              <TopIssuersChart />
            </div>
          </Section>
        )}
      </div>
    </ChartsProvider>
  );
}
